import { Suspense, useContext, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  IonAvatar,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonLoading,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonSkeletonText,
  IonSpinner,
  IonText,
  IonThumbnail,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { arrowBack } from "ionicons/icons";
import useSWR from "swr";
import ApplicationContext from "../Context";
import type { Payments } from "../types/outlet";
import "../styles/views/payment-method.css";
import { useApi } from "../utils/useAPI";
import { Cart } from "../types/cart";
import useLocalStorage from "../utils/localstorage";

function currentDateFormat(date: Date) {
  let d = new Date(date),
    day = String(d.getDate()),
    month = String(d.getMonth() + 1),
    year = d.getFullYear();
  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;
  return [year, month, day].join("-");
}

export default function PaymentMethod() {
  const { authentication, checkout, transaction } = useContext(ApplicationContext);
  const history = useHistory();
  const { code } = useParams<{ code: string }>();
  const [selectedPaymentCode, setSelectedPaymentCode] = useState<string>();
  const { apiFetch } = useApi();
  const saved_promo_code = JSON.parse(localStorage.getItem("promo-code") || "null");
  const [cart] = useLocalStorage("checkout", {} as Cart);

  const {
    data: payments,
    error: isError,
    isLoading,
    isValidating,
  } = useSWR<Payments, Error>(
    "/api/pos-order/outlet-service/available-payment",
    async (url: string) =>
      await apiFetch(process.env.REACT_APP_API_URL + url, {
        body: JSON.stringify({
          item:
            cart?.products?.map((product, index) => ({
              id_custom: index,
              id_brand: product.id_brand,
              id_product: product.id,
              product_code: product.code,
              product_name: product.name,
              product_price: product.price,
              product_price_total: (product.quantity || 1) * product.price,
              qty: product.quantity,
            })) || [],
          item_service:
            cart?.services?.map((service, index) => ({
              booking_date: currentDateFormat(new Date(Date.now())),
              id_brand: service.id_brand,
              id_custom: index,
              id_product: service.id,
              product_code: service.code,
              product_name: service.name,
              product_price: service.price,
              qty: service.quantity,
            })) || [],
          promo_code: saved_promo_code,
          outlet_code: code,
          phone: authentication.phoneNumber || null,
          pos_order: 1,
          show_all: 1,
        }),
        headers: {
          Authorization: `${authentication.token.type} ${authentication.token.access}`,
          ContentType: "application/json",
        },
        method: "POST",
      }),
    {
      dedupingInterval: 0,
      revalidateOnFocus: false,
      fallbackData: undefined,
    }
  );

  const paymentDetail = payments?.result?.find((payment) => payment.code === selectedPaymentCode)?.payment_method as string;
  const paymentType = payments?.result?.find((payment) => payment.code === selectedPaymentCode)?.payment_gateway as string;

  return (
    <IonPage className="view-payment-method">
      <IonHeader mode="md">
        <IonToolbar mode="md">
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/${code}/confirmation-order`} icon={arrowBack} mode="md" />
          </IonButtons>
          <IonTitle>
            <Suspense fallback={<IonSkeletonText animated={true} />}>
              <IonImg alt={process.env.REACT_APP_NAME} src="assets/images/logo.png" title={process.env.REACT_APP_NAME} />
            </Suspense>
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      {isLoading || isValidating ? (
        <IonGrid style={{ width: "100%" }}>
          <IonRow>
            {new Array(2).fill("")?.map((_, index) => (
              <IonGrid>
                <IonItem>
                  <IonThumbnail slot="start">
                    <IonSkeletonText animated={true}></IonSkeletonText>
                  </IonThumbnail>
                  <IonLabel>
                    <IonSkeletonText animated={true} style={{ width: "80%" }}></IonSkeletonText>
                  </IonLabel>
                </IonItem>
              </IonGrid>
            ))}
          </IonRow>
          <IonRow>
            {new Array(2).fill("")?.map((_, index) => (
              <IonGrid>
                <IonItem>
                  <IonThumbnail slot="start">
                    <IonSkeletonText animated={true}></IonSkeletonText>
                  </IonThumbnail>
                  <IonLabel>
                    <IonSkeletonText animated={true} style={{ width: "80%" }}></IonSkeletonText>
                  </IonLabel>
                </IonItem>
              </IonGrid>
            ))}
          </IonRow>
        </IonGrid>
      ) : (
        <IonContent className="ion-padding">
          <IonGrid>
            <IonRow>
              <IonCol size="12">
                <IonText mode="md">
                  <h2>PILIH METODE PEMBAYARAN</h2>
                </IonText>
                <IonText className="total">
                  <span>Total</span>
                  <h3 className="ion-no-margin">
                    {new Intl.NumberFormat("id-ID", { currency: "IDR", minimumFractionDigits: 0, style: "currency" })
                      .format(checkout.state.grandtotal)
                      .replace(/^(\D+)/, "$1 ")
                      .replace(/\s+/, " ")}
                  </h3>
                </IonText>
                <hr className="dashed" />
                <IonRadioGroup onIonChange={(e) => setSelectedPaymentCode(e.detail.value)} value={selectedPaymentCode}>
                  <IonGrid className="ion-no-padding">
                    <IonRow>
                      <IonCol size="12" sizeSm="6">
                        {isError || isLoading
                          ? new Array(10).fill("").map((_, index) => (
                              <IonItem className="ion-no-padding" key={index} lines="none" mode="md">
                                <IonSkeletonText animated={true} slot="start" style={{ "--border-radius": "50%", height: "1.5rem", width: "1.5rem" }} />
                                <IonAvatar slot="start">
                                  <IonSkeletonText animated={true} />
                                </IonAvatar>
                                <IonLabel mode="md">
                                  <IonSkeletonText animated={true} />
                                </IonLabel>
                              </IonItem>
                            ))
                          : payments?.result
                              ?.filter((payment) => payment.code !== "cash")
                              .map((payment, index) => (
                                <IonItem className="ion-no-padding" key={index} lines="none" mode="md">
                                  <IonRadio aria-label={payment.code} color="dark" mode="md" slot="start" value={payment.code} />
                                  <IonAvatar slot="start">
                                    <IonImg alt={payment.text} src={payment.logo} />
                                  </IonAvatar>
                                  <IonLabel mode="md">
                                    <strong>{payment.text}</strong>
                                  </IonLabel>
                                </IonItem>
                              ))}
                      </IonCol>
                      <IonCol size="12" sizeSm="6">
                        {isError || isLoading
                          ? new Array(1).fill("").map((_, index) => (
                              <IonItem className="ion-no-padding" key={index} lines="none" mode="md">
                                <IonSkeletonText animated={true} slot="start" style={{ "--border-radius": "50%", height: "1.5rem", width: "1.5rem" }} />
                                <IonAvatar slot="start">
                                  <IonSkeletonText animated={true} />
                                </IonAvatar>
                                <IonLabel mode="md">
                                  <IonSkeletonText animated={true} />
                                </IonLabel>
                              </IonItem>
                            ))
                          : payments?.result
                              ?.filter((payment) => payment.code === "cash")
                              .map((payment, index) => (
                                <IonItem className="ion-no-padding" key={index} lines="none" mode="md">
                                  <IonRadio aria-label={payment.code} color="dark" mode="md" slot="start" value={payment.code} />
                                  <IonAvatar slot="start">
                                    <IonImg alt={payment.text} src={payment.logo} title={payment.text} />
                                  </IonAvatar>
                                  <IonLabel className="ion-no-margin" mode="md">
                                    <strong>{payment.text}</strong>
                                  </IonLabel>
                                </IonItem>
                              ))}
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonRadioGroup>
                <hr className="dashed" />
              </IonCol>
            </IonRow>
          </IonGrid>
          <IonGrid className="submit">
            <IonRow className="ion-justify-content-center">
              <IonCol size="12" sizeSm="6" sizeMd="4">
                <IonButton
                  color="dark"
                  disabled={selectedPaymentCode === undefined || transaction.loading}
                  expand="block"
                  onClick={async () => {
                    await transaction.handle.new(paymentDetail, paymentType, async (id, redirect_url) => {
                      if (redirect_url !== undefined) {
                        window.location.replace(redirect_url);
                      } else {
                        history.replace(`/${code}/qr-code?id_transaction=${id}`);
                      }
                    });
                  }}
                  mode="md"
                  strong={true}
                >
                  {transaction.loading ? <IonSpinner className="ion-margin-end" name="crescent" slot="start" /> : null}
                  Bayar
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      )}
    </IonPage>
  );
}
